import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/authConfig';
import { logger } from '../utils/logger';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  
  // Get user information
  const account = accounts[0];
  const userName = account?.name || account?.username || 'User';

  const isActive = (path: string) => location.pathname === path;

  const navItems = [
    { path: '/', label: 'Home' },
  ];

  // Falls der Benutzer angemeldet ist, zeigen wir den Lab Days-Link
  if (isAuthenticated) {
    navItems.push({ path: '/labdays', label: 'Lab Days' });
  }

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch(error => {
      logger.error('Login fehlgeschlagen:', error);
      logger.error('Error details:', {
        errorCode: error.errorCode,
        errorMessage: error.errorMessage,
        subError: error.subError,
        correlationId: error.correlationId
      });
    });
  };

  const handleLogout = () => {
    try {
      // Clear all cached tokens and accounts locally
      instance.clearCache();
      
      // Remove any stored redirect URLs
      localStorage.removeItem('loginRedirectUrl');
      
      // Force a page reload to ensure clean state
      window.location.href = '/';
    } catch (error) {
      logger.error('Logout fehlgeschlagen:', error);
      // Fallback: just reload the page
      window.location.reload();
    }
  };

  return (
    <nav className="bg-white shadow-md">
      <div className="container mx-auto px-4">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <img src="/open-logo.png" alt="Open Logo" className="h-10 w-10" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-shopware-blue text-white'
                    : 'text-gray-700 hover:bg-shopware-light-blue'
                }`}
              >
                {item.label}
              </Link>
            ))}

            {isAuthenticated ? (
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-shopware-dark-blue">
                  Welcome, {userName}
                </span>
                <button
                  onClick={handleLogout}
                  className="px-3 py-2 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100"
                >
                  Abmelden
                </button>
              </div>
            ) : (
              <button
                onClick={handleLogin}
                className="ml-4 px-3 py-2 rounded-xl text-sm font-medium bg-shopware-light-blue text-shopware-dark-blue hover:bg-shopware-blue hover:text-white"
              >
                Anmelden
              </button>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-xl text-gray-700 hover:bg-shopware-light-blue focus:outline-none"
            >
              {isOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`block px-3 py-2 rounded-xl text-base font-medium ${
                  isActive(item.path)
                    ? 'bg-shopware-blue text-white'
                    : 'text-gray-700 hover:bg-shopware-light-blue'
                }`}
                onClick={() => setIsOpen(false)}
              >
                {item.label}
              </Link>
            ))}

            {isAuthenticated ? (
              <div className="space-y-2">
                <div className="px-3 py-2 text-base font-medium text-shopware-dark-blue">
                  Welcome, {userName}
                </div>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-3 py-2 rounded-xl text-base font-medium text-gray-700 hover:bg-gray-100"
                >
                  Abmelden
                </button>
              </div>
            ) : (
              <button
                onClick={handleLogin}
                className="block w-full text-left px-3 py-2 rounded-xl text-base font-medium bg-shopware-light-blue text-shopware-dark-blue hover:bg-shopware-blue hover:text-white"
              >
                Anmelden
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation; 